import {injectIntl} from 'react-intl';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {cloneDeep} from 'lodash';
import Util from '../../../forecast-app/shared/util/util';
import ProjectUtil from '../../../forecast-app/shared/util/ProjectUtil';
import DatePicker from '../../../forecast-app/shared/components/date-picker/date_picker_v3';
import {DATE_PICKER_STYLE, ESTIMATION_UNIT} from '../../../constants';
import NumericTile from '../numeric_tile';
import Dropdown from '../../../forecast-app/shared/components/dropdowns/dropdown';
import AssignedTableTile from '../../../forecast-app/shared/components/table-tiles/assigned_table_tile';
import TextTile from '../text_tile';
import UpdateTaskMutation from '../../../mutations/update_task_mutation.modern';
import AssignPersonToTaskMutation, {handleAssignPersonToTaskErrors} from '../../../mutations/AssignPersonToTaskMutation';
import UnassignPersonFromTaskMutation from '../../../mutations/UnassignPersonFromTaskMutation';
import CheckBox from '../check_box';
import {WarningIcon} from '../../../images/WarningIcon';
import TaskTableInputField from './table-row-elements/task_table_input_field';
import * as tracking from '../../../tracking';
import StarTask from '../../../forecast-app/shared/components/star/star_task';
import Moment from 'moment';
import TimeRegPopup from '../../../forecast-app/shared/components/popups/time_reg_popup';
import {HoursInput} from '../../../forecast-app/shared/components/inputs/hours-input/hours_input_view';
import {AffixedInputWrapper} from '../../../forecast-app/shared/components/inputs/AffixedInputWrapper';
import NumericTiles from './numeric-tiles/numeric_tiles_view';
import LabelGroup from '../../../forecast-app/shared/components/labels/label_group';
import {
	hasPermission,
	hasTaskStatusChangePermission,
	hasTaskUpdatePermission,
} from '../../../forecast-app/shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../../Permissions';
import {PredictionPopupInputExtension} from '../../../forecast-app/shared/components/task-tables/task-table-v3/elements/task-row-elements/prediction-popup/PredictionPopupInputExtension';
import {showOverrunPredictionWarning} from '../../../forecast-app/shared/util/PredictionUtil';
import {VARIANT} from '../../../containers/modal/project-automate/project_automate_modal';
import {TaskParentName} from './table_row.styled';
import {
	NumericTileV2,
	UNIT,
} from '../../../forecast-app/shared/components/task-tables/task-table-v3/elements/task-row-elements/NumericTile';
import {TaskProgressRegistrationPopup} from '../../../forecast-app/shared/components/popups/progress-registration/TaskProgressRegistrationPopup';
import {trackEvent} from '../../../tracking/amplitude/TrackingV2';
import {isDateDisabled} from '../../../forecast-app/shared/util/DateUtil';
import DeprecatedProjectIndicatorJS from '../../../forecast-app/shared/components/project-indicator/js/DeprecatedProjectIndicatorJS';
import {projectUrl} from '../../../directApi';
import {getTotalFromTimeRegs} from './time_reg_util';
import {SprintNovaControl} from '../../../forecast-app/shared/components/nova-invocation/SprintNovaControl';
import {NOVA_RUN_SURFACE} from '../../../forecast-app/shared/components/nova-invocation/novaRunSurface';
import {NOVA_INVOCATION_COLUMN_WIDTH} from '../../../forecast-app/shared/components/nova-invocation/styleTokens';
import {isProjectDone, isProjectLocked} from '../../../forecast-app/shared/util/projectStageUtil';

/**
 * The width of the cell that holds the row's leading cluster - the selector, the Nova control, the
 * task id and the name.
 *
 * Widened by the Nova column rather than letting the column come out of the name. Taken out of the
 * name instead, a narrow table leaves the name nothing at all, and the header - whose matching cell
 * holds a checkbox, a label and a task heading in the same space - shrinks the only cell there that
 * can give way, carrying every label after it left of the column it names. The page's width budget
 * has already set this column's share aside, so the table still fits.
 */
export const leadingCellWidth = (taskCellMaxWidth, hasNovaControl) =>
	taskCellMaxWidth + (hasNovaControl ? NOVA_INVOCATION_COLUMN_WIDTH : 0);

class TableRow extends Component {
	constructor(props) {
		super(props);

		this.state = {
			newTaskInput: '',
			remainingInput: props.task ? props.task.timeLeft : null,
		};
		this.getElemForCol = this.getElemForCol.bind(this);
	}

	UNSAFE_componentWillReceiveProps(nextProps) {
		// Don't update if our estimate fields currently have focus

		if (this.getEstimateInputsFocused() || nextProps.task) {
			this.setState({
				remainingInput: nextProps.task.timeLeft,
			});
		}
	}

	shouldComponentUpdate(nextProps) {
		return !(this.props.dragActive && nextProps.dragActive);
	}

	getEstimateInputsFocused() {
		const activeElem = document.activeElement;
		if (activeElem) {
			return activeElem.id.includes('estimate');
		}
	}

	handleSelected(e) {
		if (this.props.disabled || this.props.projectLocked) {
			return;
		}
		if (this.props.onRowSelected) {
			this.props.onRowSelected(this.props.id, e.shiftKey, e.ctrlKey, this.props.emptyPhase);
		}
		if (this.state.showContextMenu) {
			this.setState({showContextMenu: false});
		}
	}

	refreshRemaining() {
		this.setState({remainingInput: this.props.task.timeLeft});
	}

	updateTaskRemaining() {
		let task = this.props.task;
		const taskProject = this.props.isConnectedParent
			? this.props.viewer.projectGroup.projects.edges.find(project => project.node.id === task.project.id).node
			: this.props.viewer.project
			? this.props.viewer.project
			: this.props.task.project;

		if (this.props.disabled || this.props.projectLocked) {
			return;
		}

		let remainingInput = this.state.remainingInput;
		const shouldUpdateRemaining = task.timeLeft !== remainingInput;
		if (shouldUpdateRemaining && !taskProject.remainingAutoCalculated) {
			const mutationObject = {
				ids: [task.id],
				remaining: remainingInput,
			};
			Util.CommitMutation(UpdateTaskMutation, mutationObject);
		}

		if (!shouldUpdateRemaining) {
			this.refreshRemaining();
		}
	}

	onDateUpdateSuccess(result) {
		if (result.updateTask.errors && result.updateTask.errors.length === 1) {
			Util.checkForSageErrorAndShowModal(result.updateTask.errors, false);
		}
	}

	clearDates() {
		if (this.props.disabled || this.props.projectLocked) {
			return;
		}
		Util.CommitMutation(
			UpdateTaskMutation,
			{
				ids: [this.props.task.id],
				startYear: null,
				startMonth: null,
				startDay: null,
				deadlineYear: null,
				deadlineMonth: null,
				deadlineDay: null,
			},
			this.onDateUpdateSuccess
		);
	}

	handleDateRangeChange(startDate, endDate) {
		if (this.props.disabled || this.props.projectLocked) {
			return;
		}
		Util.CommitMutation(
			UpdateTaskMutation,
			{
				ids: [this.props.task.id],
				startYear: startDate.year(),
				startMonth: startDate.month() + 1,
				startDay: startDate.date(),
				deadlineYear: endDate.year(),
				deadlineMonth: endDate.month() + 1,
				deadlineDay: endDate.date(),
			},
			this.onDateUpdateSuccess
		);
	}

	handleInputChange(key, val) {
		const task = this.props.task;
		if (this.props.disabled || this.props.projectLocked || isProjectLocked(task.project)) {
			return;
		}
		const inputVal = this.props.isEstimatedInHours
			? Util.getMinuteEstimate(Number(val))
			: this.props.task.project.estimationUnit === 'HOURS'
			? Util.getMinuteEstimate(Number(val))
			: Number(val);
		this.setState({[key]: inputVal});
	}

	onEstimateUpdateSuccess(result) {
		if (result.updateTask.errors && result.updateTask.errors.length === 1) {
			Util.checkForSageErrorAndShowModal(result.updateTask.errors, false);
		}
	}

	handleEstimationChanges(key, val) {
		if (isNaN(val)) return;
		const valRounded = Math.round(val); // Round to avoid edge cases caused by JS floating point representation

		const task = this.props.task;

		if (task.estimateForecast === valRounded) return;
		const latestOverrunPredictionTask = localStorage.getItem('latest-overrun-prediction-shown');
		if (latestOverrunPredictionTask === task.id) {
			tracking.trackAIEvent('Recently seen overrun reestimated', VARIANT.ESTIMATES, null, {
				appliedEstimate: valRounded,
				predictedEstimate: task.predictedEstimate?.estimate,
				taskId: task.id,
				taskName: task.name,
			});
			trackEvent('Recently Inspected Predicted Overrun Task', 'Reestimated', {
				appliedEstimate: valRounded,
				predictedEstimate: task.predictedEstimate?.estimate,
				taskId: task.id,
				taskName: task.name,
			});
			localStorage.removeItem('latest-overrun-prediction-shown');
		}
		const mutation = {
			ids: [task.id],
			forecast: valRounded,
		};
		Util.CommitMutation(UpdateTaskMutation, mutation, this.onEstimateUpdateSuccess);

		this.setState({[key]: val});
	}

	handleRemainingChange(val) {
		Util.CommitMutation(UpdateTaskMutation, {ids: [this.props.task.id], remaining: val});
	}

	onTaskApprovalChange(task, option) {
		if (this.props.disabled || this.props.projectLocked) {
			return;
		}
		Util.CommitMutation(UpdateTaskMutation, {
			ids: [task.id],
			approved: option.value,
		});
	}

	toggleContextMenu(e) {
		if (!this.props.lazyDataFetched || this.props.disabled || this.props.projectLocked) {
			return;
		}

		e.preventDefault();
		e.stopPropagation();
		this.props.onContextMenu(this.props.task, e);
	}

	onRowBlur() {
		this.setState({showContextMenu: false});
	}

	assignPersonsToTask(personId) {
		const task = this.props.task;
		if (this.props.disabled || this.props.projectLocked) {
			return;
		}
		if (task && personId) {
			Util.CommitMutation(
				AssignPersonToTaskMutation,
				{
					taskId: task.id,
					personId: personId,
				},
				handleAssignPersonToTaskErrors
			);
		}
	}

	assignRoleToTask(role) {
		let task = this.props.task;
		if (this.props.disabled || this.props.projectLocked) {
			return;
		}
		Util.CommitMutation(UpdateTaskMutation, {
			ids: [task.id],
			roleId: role,
		});
	}

	setOwner(deleteNode, personId) {
		Util.CommitMutation(UpdateTaskMutation, {
			ids: [this.props.task.id],
			ownerId: Array.isArray(personId) ? personId[0] : personId,
			deleteNode,
		});
	}

	unassignPersonFromTask(personId) {
		let task = this.props.task;
		if (this.props.disabled || this.props.projectLocked) {
			return;
		}
		if (task && personId) {
			Util.CommitMutation(UnassignPersonFromTaskMutation, {
				taskId: task.id,
				personId: personId,
			});
		}
	}

	validateRemaining() {
		return Number(this.state.remainingInput) >= 0;
	}

	onStatusColumnChange(option) {
		const {task, intl} = this.props;
		Util.changeTaskStatus(task, option, intl);
	}

	onSprintChange(selected) {
		const onSuccess = response => {
			if (
				response &&
				response.updateTask &&
				response.updateTask.errors &&
				response.updateTask.errors.includes('jira_sprint_change_failed')
			) {
				// Jira rejected the sprint change. Show message to user.
				Util.showJiraSprintChangeRejected(this.props.intl);
			}
		};

		const mutationObject = {
			ids: [this.props.task.id],
		};

		if (this.props.viewer.project) {
			mutationObject.sprintId = selected ? selected.value : null;
		} else {
			mutationObject.projectGroupSprintId = selected ? selected.value : null;
		}
		mutationObject.projectId = this.props.task.project.id;
		mutationObject.pageContext = this.props.parentType;
		mutationObject.prevStates = [{node: this.props.task}];
		Util.CommitMutation(UpdateTaskMutation, mutationObject, onSuccess);
	}

	onPhaseChange(selected) {
		const phaseId = selected ? selected.value : null;
		Util.CommitMutation(UpdateTaskMutation, {
			ids: [this.props.task.id],
			phaseId: phaseId,
		});
	}

	onTaskClick(e, task, elem) {
		if (elem !== 'div' && !this.props.selectionMode && !e.shiftKey) {
			const newTarget = e.relatedTarget || e.explicitOriginalTarget;
			if (newTarget && (newTarget.tagName === 'INPUT' || newTarget.tagName === 'Button')) {
				return;
			}
			tracking.trackElementClicked('Task name');
			trackEvent('Task Name', 'Clicked');
			this.openCardModal(e, task);
		}
	}

	openCardModal(e, item) {
		e.stopPropagation();
		e.preventDefault();
		if (this.props.showTaskModal) {
			this.props.showTaskModal(item.companyTaskId);
		}
	}

	getNumberOfIconsForTask(task) {
		const hasWarning = Util.GetTaskWarnings(task) != null;
		// Boolean to number conversion, woooh!
		return task.bug + task.blocked + task.highPriority + hasWarning;
	}

	getProjectNavigationString(project) {
		const curLocArr = window.location.href.split('/');
		const pageName = curLocArr[curLocArr.length - 1];
		return projectUrl(project.companyProjectId, project.customProjectId) + '/' + pageName;
	}

	getDeadlineTextForTask(task) {
		const {formatMessage} = this.props.intl;
		if (task.statusColumnV2 && task.statusColumnV2.category === 'DONE') {
			return <span className={'done date-text'}>{formatMessage({id: 'common.done'})}</span>;
		}
		const today = Moment();
		if (task.deadlineDay == null) {
			return '';
		}
		const taskDeadline = Util.CreateMomentDate(task.deadlineYear, task.deadlineMonth, task.deadlineDay);
		if (today.isSame(taskDeadline, 'date')) {
			return <span className={'today date-text'}>{formatMessage({id: 'common.today'})}</span>;
		} else if (today.add(1, 'days').isSame(taskDeadline, 'date')) {
			return <span className={'today date-text'}>{formatMessage({id: 'common.tomorrow'})}</span>;
		} else if (today.isAfter(taskDeadline, 'date')) {
			return <span className={'overdue date-text'}>{taskDeadline.format('DD MMM')}</span>;
		}
		return <span className={'date-text'}>{taskDeadline.format('DD MMM')}</span>;
	}

	/**
	 * The Nova cell of the row's leading cluster, or nothing when the column is not being drawn.
	 *
	 * A method rather than inline in render, for the same reason getElemForCol below is one: render
	 * needs a page's worth of props before it will run, and this cell's contract - a live control
	 * only for a column that is actually on, carrying the instance key that keeps it apart from the
	 * same task's control on a backlog card - is worth stating where it can be read and exercised on
	 * its own.
	 */
	getNovaControlForColumn(col, task) {
		// Only when the column is actually shown. Render admits a column that is merely holding its
		// space open, which for every other column means an empty cell - for this one it would mean a
		// live control in a column the header is not drawing.
		if (!col.checked) {
			return null;
		}
		return (
			<div className={'nova-row-elem'} key={'nova'} style={{width: NOVA_INVOCATION_COLUMN_WIDTH}}>
				{/* Distinct from every other mounting surface, so two controls for one task on one page
				    - a sprint row and the backlog card beside it - keep their own caret ids and their
				    own picker. */}
				<SprintNovaControl
					task={task}
					instanceKey="sprint-row"
					surface={NOVA_RUN_SURFACE.SPRINTS}
					readOnly={this.props.disabled}
					onOpenTask={() => this.props.showTaskModal(task.companyTaskId)}
				/>
			</div>
		);
	}

	getElemForCol(col, taskId, selector, novaControl) {
		const {formatMessage, formatNumber} = this.props.intl;
		const task = this.props.task;
		const taskProject = this.props.isConnectedParent
			? this.props.viewer.projectGroup.projects.edges.find(project => project.node.id === task.project.id).node
			: this.props.viewer.project
			? this.props.viewer.project
			: task.project;
		const taskCellMaxWidth = this.props.taskCellMaxWidth;
		const colName = col.name;
		const groupByPerson = this.props.groupByPerson || this.props.groupByRole;

		const totalTime = task.timeRegistrations
			? getTotalFromTimeRegs(
					task.timeRegistrations.edges,
					false,
					timeReg => timeReg.node.minutesRegistered,
					this.props.groupByRole
			  ) / 60
			: 0;
		const totalBillableTime = task.timeRegistrations
			? getTotalFromTimeRegs(
					task.timeRegistrations.edges,
					false,
					timeReg => timeReg.node.billableMinutesRegistered,
					this.props.groupByRole
			  ) / 60
			: 0;
		const totalNonBillableTime = totalTime - totalBillableTime;

		const personTimeRegs =
			groupByPerson && task.timeRegistrations
				? getTotalFromTimeRegs(
						task.timeRegistrations.edges,
						this.props.groupPersonId,
						timeReg => timeReg.node.minutesRegistered,
						this.props.groupByRole
				  ) / 60
				: 0;

		const personBillableTimeRegs =
			groupByPerson && task.timeRegistrations
				? getTotalFromTimeRegs(
						task.timeRegistrations.edges,
						this.props.groupPersonId,
						timeReg => timeReg.node.billableMinutesRegistered,
						this.props.groupByRole
				  ) / 60
				: 0;
		const personNonBillableTimeRegs = personTimeRegs - personBillableTimeRegs;

		const forecast =
			task && task.project
				? task.project.estimationUnit === ESTIMATION_UNIT.HOURS
					? task.estimateForecast / 60
					: task.estimateForecast
				: this.props.isEstimatedInHours
				? task.estimateForecast / 60
				: task.estimateForecast;
		const disableActions =
			!this.props.lazyDataFetched || this.props.projectLocked || this.props.disabled || this.props.selectionMode;
		// Restrictable fields (role, assignees, owner, dates, phase, sprint, estimate, approved)
		// additionally require the task-editing permission. Progress, the row selector and the
		// time-registration/remaining columns are not restrictable and keep using
		// `disableActions` alone.
		const disableEditableActions = disableActions || !hasTaskUpdatePermission();
		// PIKA-790: status has its own permission and its own feature flag. Deliberately NOT
		// folded into disableEditableActions — the two permissions are independent in both
		// directions, so a single flag could not express the combination.
		const disableStatusActions = disableActions || !hasTaskStatusChangePermission();
		const numberOfPerson = this.props.numPersonsAssignedToTask != null ? this.props.numPersonsAssignedToTask : 1;
		const isShared = numberOfPerson > 1;

		const taskAssignedNames =
			task.assignedPersons.length > 2 // Oxford comma rule of three
				? task.assignedPersons
						.map(person => person.firstName + ' ' + person.lastName)
						.reduce(
							(acc, name, index) =>
								index === task.assignedPersons.length - 1
									? acc.concat(' and ' + name)
									: acc.concat(name + ', '),
							''
						)
				: task.assignedPersons
						.map(person => person.firstName + ' ' + person.lastName)
						.reduce(
							(acc, name, index) =>
								index === task.assignedPersons.length - 1 ? acc.concat(' and ' + name) : acc.concat(name + ' '),
							''
						);

		const usePoints = !this.props.isEstimatedInHours;
		const estimateSharedMessage = formatMessage(
			{id: 'common.estimate_shared'},
			{
				total: usePoints ? forecast : Util.convertMinutesToFullHour(forecast * 60, this.props.intl),
				persons: taskAssignedNames,
			}
		);
		const showWarning = showOverrunPredictionWarning(task);
		const predictionInputExtension = showWarning ? (
			<PredictionPopupInputExtension
				task={task}
				modelScore={this.props.lowHighModelScore}
				showWarning={showWarning}
				intl={this.props.intl}
			/>
		) : null;

		if (!col.checked && col.maintainSpace) {
			return null;
		}
		switch (colName) {
			case 'selector':
				return (
					<CheckBox
						id={task.id}
						key={'selector'}
						isDisabled={isProjectDone(task.project)}
						isChecked={this.props.selected}
						isFocusable={!isProjectDone(task.project)}
						onClick={this.handleSelected.bind(this)}
						cy={'selector-checkbox'}
						userpilot={'bulk-update-checkbox'}
					/>
				);
			case 'project-id':
				return <DeprecatedProjectIndicatorJS project={taskProject} />;
			case 'project-name':
				return (
					<div className={'project-group'}>
						<div className={'project-name'}>
							{disableActions ? (
								<div className={'td-text no-click'} tabIndex={'0'} title={taskProject.name}>
									{taskProject.name}
								</div>
							) : (
								<a
									className={'td-text'}
									href={this.getProjectNavigationString(taskProject)}
									tabIndex={'0'}
									title={taskProject.name}
								>
									{taskProject.name}
								</a>
							)}
						</div>
					</div>
				);
			case 'task-name':
				// 15px icon width, 2px margin
				const iconWidth = 17;
				const warnings = Util.GetTaskWarnings(task, this.props.intl);
				const taskIdWidth = !!taskId ? 50 : 0;
				const selectorWidth = !!selector ? 50 : 0;
				// The Nova control sits in this same leading cluster, so the cluster reserves its width -
				// and, unlike the selector and the task id beside it, the name does not give that width
				// back. The cell around the cluster is widened by the same amount instead (see the name's
				// own widths below, and leadingCellWidth). Subtracting it here as well would take the
				// column out of the name twice.
				const novaWidth = !!novaControl ? NOVA_INVOCATION_COLUMN_WIDTH : 0;
				const totalIconWidth = this.getNumberOfIconsForTask(task) * iconWidth;
				const groupPadding = 16;

				// Parent task name
				const parentTaskName = task.project.useTaskHierarchy && task.parentTask?.name;

				return (
					<div
						className={'task-group' + (this.props.showProjectIndicator ? ' not-first' : '')}
						style={{
							maxWidth: taskCellMaxWidth + selectorWidth + novaWidth,
							minWidth: taskCellMaxWidth + selectorWidth + novaWidth,
						}}
					>
						{selector ? selector : null}
						{novaControl ? novaControl : null}
						{taskId ? taskId : null}
						{task && warnings && warnings[0] ? (
							<div className={'warning-icon'}>
								<WarningIcon
									title={warnings[0].message}
									danger={warnings[0].color === 'red'}
									customWidth={15}
									customHeight={15}
								/>
							</div>
						) : null}
						{task && task.highPriority ? <div className={'high-priority-flag'} /> : null}
						{task && task.blocked ? <div className={'blocked-icon'} /> : null}
						{task && task.bug ? <div className={'bug-icon'} /> : null}
						<div
							className={'task-name' + (task && task.highPriority ? ' with-flag' : '')}
							style={{
								// Nova is deliberately not subtracted here: the cluster's own box is widened by
								// exactly that much, so the name keeps the width it had before the column existed.
								maxWidth: taskCellMaxWidth
									? taskCellMaxWidth - selectorWidth - taskIdWidth - totalIconWidth - groupPadding
									: '',
								minWidth: taskCellMaxWidth
									? taskCellMaxWidth - selectorWidth - taskIdWidth - totalIconWidth - groupPadding
									: '',
							}}
						>
							{disableActions ? (
								<div
									className={'td-text'}
									tabIndex={'0'}
									onKeyDown={
										task
											? e => (e.key === ' ' || e.key === 'Enter' ? this.openCardModal(e, task) : null)
											: null
									}
									onClick={task ? e => this.onTaskClick(e, task) : null}
									title={task.name}
								>
									{parentTaskName && <TaskParentName>{parentTaskName}</TaskParentName>}
									{task.name}
								</div>
							) : (
								<a
									className={'td-text'}
									data-cy="task-name"
									href={
										window.location.href +
										(window.location.href.endsWith('/')
											? 'T' + task.companyTaskId
											: '/T' + task.companyTaskId)
									}
									tabIndex={'0'}
									onKeyDown={
										task
											? e => (e.key === ' ' || e.key === 'Enter' ? this.openCardModal(e, task) : null)
											: null
									}
									onClick={task ? e => this.onTaskClick(e, task) : null}
									title={task.name}
								>
									{parentTaskName && <TaskParentName>{parentTaskName}</TaskParentName>}
									{task.name}
								</a>
							)}
						</div>
					</div>
				);
			case 'forecast': //estimate
				const forecastValue = forecast;
				if (isShared) {
					const personForecastValueInHoursShared = Math.round((forecast / numberOfPerson) * 100) / 100;

					if (usePoints) {
						return (
							<NumericTile
								task={task}
								id={task.id}
								key={'estimate'}
								value={personForecastValueInHoursShared}
								unit={this.estimationUnit}
								isV2={true}
								cy={'forecast-tile'}
								groupedValues={isShared}
								tooltip={isShared}
								infoText={
									isShared
										? '' +
										  formatMessage(
												{id: 'common.estimate_shared'},
												{total: personForecastValueInHoursShared, persons: taskAssignedNames}
										  )
										: ''
								}
							/>
						);
					} else {
						return (
							<NumericTiles
								id={task.id}
								key={'estimate'}
								valueInMinutes={personForecastValueInHoursShared * 60}
								asterisk={isShared}
								infoText={formatMessage(
									{id: 'common.estimate_shared'},
									{
										total: Util.convertMinutesToFullHour(forecast * 60, this.props.intl),
										persons: taskAssignedNames,
									}
								)}
								cy={'forecast-tile'}
							/>
						);
					}
				} else {
					return this.props.lazyDataFetched && usePoints ? (
						<AffixedInputWrapper
							onClick={e => e.focus()}
							disabled={disableEditableActions || this.props.isJiraProject}
							customClassName={'hours-input'}
							value={forecastValue}
							callback={value => this.handleEstimationChanges('estimateInput', value)}
							affix={formatMessage({id: 'common.points.short'})}
						/>
					) : this.props.lazyDataFetched ? (
						<div className="hours-input-wrapper">
							{predictionInputExtension}
							<HoursInput
								value={forecastValue}
								mutation={v => this.handleEstimationChanges('estimateInput', v * 60)}
								onClick={e => e.focus()}
								cy={'estimate-input'}
								disabled={disableEditableActions || this.props.isJiraProject}
								withInputExtension={showWarning}
							/>
						</div>
					) : (
						<NumericTiles
							id={task.id}
							valueInMinutes={forecastValue * 60}
							asterisk={isShared}
							infoText={isShared ? estimateSharedMessage : null}
							cy={'forecast-tile'}
						/>
					);
				}
			case 'done-percentage':
				const progressRegistrationPopup =
					!disableActions && !this.props.isClientUser ? <TaskProgressRegistrationPopup task={task} /> : null;

				return task.done ? (
					<div className={'done-check-mark'} />
				) : taskProject.manualProgressOnTasksEnabled ? (
					<NumericTileV2
						id={task.id}
						key={'progress'}
						popup={progressRegistrationPopup}
						value={task.progressDetails?.progress || 0}
						unit={UNIT.PERCENTAGE}
						cy={'done-percentage-tile'}
					/>
				) : task.progress !== undefined ? (
					<NumericTile
						task={task}
						id={task.id}
						key={'forecast'}
						value={task.progress}
						unit={'%'}
						isV2={true}
						cy={'done-percentage-tile'}
					/>
				) : (
					<TextTile textAlign="right" id={task.id} key={'time-entries'} value={'-'} />
				);
			case 'assigned-role':
				return (
					<AssignedTableTile
						disabled={disableEditableActions}
						assignableRoles={this.props.assignableRoles}
						assignedRole={task.role}
						assignRole={this.assignRoleToTask.bind(this)}
						roleAssigned={task.role ? task.role.name : ''}
						useTableStyling={true}
						isMultiSelect={true}
						id={task.id}
						key={'assigned-role'}
						task={task}
						maxDisplayed={1}
						cy={'assigned-role'}
						viewer={this.props.viewer}
						noClear={true}
						closeOnChoice={true}
					/>
				);
			case 'assigned-person':
				return (
					<AssignedTableTile
						disabled={disableEditableActions}
						assignablePersons={
							this.props.assignablePersons
								? this.props.assignablePersons
								: task.project && task.project.projectPersons
								? task.project.projectPersons.edges
								: []
						}
						assignedPersons={task.assignedPersons}
						assignPersonOnlyDiff={true}
						assignPerson={this.assignPersonsToTask.bind(this)}
						unassignPerson={this.unassignPersonFromTask.bind(this)}
						useTableStyling={true}
						isMultiSelect={true}
						id={task.id}
						key={'assigned-person'}
						task={task}
						maxDisplayed={this.props.entityId === 'all-tasks' ? 2 : 4}
						cy={'assigned-person'}
						viewer={this.props.viewer}
						suggestedPersonRoleFilter={task.role}
						isClientUser={this.props.isClientUser}
					/>
				);
			case 'task-owner':
				return (
					<AssignedTableTile
						disabled={disableEditableActions || !task.project.useTaskOwner || this.props.isClientUser}
						assignablePersons={
							this.props.assignablePersons
								? this.props.assignablePersons
								: task.project && task.project.projectPersons
								? task.project.projectPersons.edges
								: []
						}
						assignedPersons={task.owner ? [task.owner] : []}
						assignPersonOnlyDiff={true}
						assignPerson={this.setOwner.bind(this, false)}
						unassignPerson={this.setOwner.bind(this, false, null)}
						useTableStyling={true}
						isMultiSelect={false}
						cy="task-modal-owner-dropdown"
						viewer={this.props.viewer}
						task={task}
						useSmallerStyling={true}
						withAddIcon={false}
						closeOnChoice={true}
						wideExpanded={true}
						isClientUser={this.props.isClientUser}
						isOwner={true}
					/>
				);
			case 'deadline': {
				return (
					<div className={'task-deadline'}>
						<div className={'task-deadline-text'}>{this.getDeadlineTextForTask(task)}</div>
					</div>
				);
			}
			case 'non-billable-time-entries':
				if (task.timeRegistrations) {
					const minutesRegistered = (groupByPerson ? personNonBillableTimeRegs : totalNonBillableTime) * 60;
					return <NumericTiles id={task.id} valueInMinutes={minutesRegistered} cy={'time-entries-tile'} />;
				}
				break;
			case 'billable-time-entries':
				if (task.timeRegistrations) {
					const minutesRegistered = (groupByPerson ? personBillableTimeRegs : totalBillableTime) * 60;
					return <NumericTiles id={task.id} valueInMinutes={minutesRegistered} cy={'time-entries-tile'} />;
				}
				break;
			case 'time-entries':
				const timeRegPopup =
					!disableActions && !this.props.isClientUser ? (
						<TimeRegPopup viewer={this.props.viewer} projectId={taskProject.id} task={task} harvestTask={false} />
					) : null;
				if (task.timeRegistrations) {
					const minutesRegistered = (groupByPerson ? personTimeRegs : totalTime) * 60;
					return (
						<NumericTiles
							id={task.id}
							valueInMinutes={minutesRegistered}
							timeRegPopup={timeRegPopup}
							cy={'time-entries-tile'}
						/>
					);
				} else {
					return <TextTile textAlign="right" id={task.id} key={'time-entries'} value={'-'} />;
				}
			case 'remaining':
				if (this.props.task.timeLeft === undefined) {
					return <TextTile textAlign="right" id={task.id} key={'remaining'} value={'-'} />;
				}
				if (usePoints) {
					const remainingValue = Math.round(this.state.remainingInput * 100) / 100;
					const personRemainingValue = groupByPerson
						? Math.round((remainingValue / numberOfPerson) * 100) / 100
						: null;
					return (
						<TaskTableInputField
							hideLabel={true}
							invalidInput={!this.validateRemaining()}
							id={task.id}
							key={'remaining'}
							cy={'remaining'}
							task={task}
							disabled={
								disableActions ||
								this.props.isJiraProject ||
								taskProject.remainingAutoCalculated ||
								task.statusColumnV2.category === 'DONE' ||
								isShared
							}
							minNumber={0}
							placeholder={'0'}
							companyEstimationUnit={this.estimationUnit}
							type="number"
							alignText={'right'}
							onBlur={this.updateTaskRemaining.bind(this)}
							value={groupByPerson ? personRemainingValue || '' : remainingValue || ''}
							onChange={v => this.handleInputChange('remainingInput', v)}
							groupedValues={isShared && remainingValue > 0}
							tooltipMessage={
								isShared && remainingValue > 0
									? formatMessage(
											{id: 'common.remaining_shared'},
											{total: remainingValue, persons: taskAssignedNames}
									  )
									: ''
							}
						/>
					);
				} else {
					const remainingValue = this.state.remainingInput;
					const remainingInMinutes = isShared
						? Math.round((remainingValue / numberOfPerson) * 100) / 100
						: this.state.remainingInput;
					if (
						disableActions ||
						this.props.isJiraProject ||
						taskProject.remainingAutoCalculated ||
						task.statusColumnV2.category === 'DONE' ||
						isShared
					) {
						return (
							<NumericTiles
								infoText={
									isShared && remainingValue > 0
										? formatMessage(
												{id: 'common.remaining_shared'},
												{
													total: Util.convertMinutesToFullHour(remainingValue, this.props.intl),
													persons: taskAssignedNames,
												}
										  )
										: ''
								}
								id={task.id}
								valueInMinutes={remainingInMinutes}
								asterisk={isShared}
							/>
						);
					} else {
						return (
							<HoursInput
								customClassName={'hours-input'}
								value={remainingInMinutes / 60}
								mutation={v => this.handleRemainingChange(v * 60)}
								onClick={e => e.focus()}
								disabled={isShared}
							/>
						);
					}
				}

			case 'status':
				if (!this.props.lazyDataFetched || !taskProject || !taskProject.statusColumnsV2) {
					return (
						<TextTile
							textAlign={col.align}
							task={task}
							id={task.id}
							key={'status'}
							value={task.statusColumnV2 ? task.statusColumnV2.name : ''}
						/>
					);
				} else {
					return (
						<Dropdown
							focusOnClick={true}
							optionClickEvent={true}
							onChange={this.onStatusColumnChange.bind(this)}
							options={cloneDeep(taskProject.statusColumnsV2.edges)
								.sort((a, b) => {
									const comparisonResult = a.node.order - b.node.order;
									if (comparisonResult > 0) {
										return 1;
									} else if (comparisonResult < 0) {
										return -1;
									} else {
										return 0;
									}
								})
								.map(statusColumn => {
									return {
										label: statusColumn.node.name,
										value: statusColumn.node.id,
										logo: statusColumn.node.jiraStatusId ? 'jira-logo' : undefined,
									};
								})}
							value={task.statusColumnV2.id}
							hideLabel={true}
							disabled={disableStatusActions || this.props.isClientUser}
							listDataCy={'status-list'}
							buttonCy={'status-arrow'}
							inputCy={'status-input'}
							restrictWidth={true}
							customHeight={30}
						/>
					);
				}
			case 'over-forecast':
				if (task.timeRegistrations) {
					const forecastInHours =
						task.project.isEstimatedInHours || this.props.isEstimatedInHours
							? forecast
							: (forecast * (task.project.minutesPerEstimationPoint || this.props.minutesPerEstimationPoint)) /
							  60;
					const overForecastValue = forecastInHours - totalTime;
					const overForecastPersonValue = forecastInHours / numberOfPerson - personTimeRegs;
					return (
						<NumericTiles
							id={task.id}
							key={'over-forecast'}
							valueInMinutes={(groupByPerson ? overForecastPersonValue : overForecastValue) * 60}
							cy={'over-forecast-tile'}
						/>
					);
				} else {
					return <TextTile textAlign="right" id={task.id} key={'over-forecast'} value={'-'} />;
				}
			case 'price':
				const forecastPriceValue = formatNumber(ProjectUtil.getEstimatedPrice(task), {
					format: 'always_two_decimal',
				});
				const forecastPersonPriceValue = formatNumber(Number(ProjectUtil.getEstimatedPrice(task) / numberOfPerson), {
					format: 'always_two_decimal',
				});
				return (
					<NumericTile
						id={task.id}
						key={
							'price' // estimateForecastPrice
						}
						placeUnitBeforeValue={this.props.placeUnitBeforeValue}
						task={task}
						value={groupByPerson ? forecastPersonPriceValue : forecastPriceValue}
						unit={this.props.currencySymbol}
						isV2={true}
						cy={'price-tile'}
						groupedValues={isShared}
						tooltip={isShared}
						infoText={
							isShared
								? formatMessage(
										{id: 'common.price_shared'},
										{total: forecastPriceValue, persons: taskAssignedNames}
								  )
								: ''
						}
					/>
				);
			case 'date':
				return (
					<div className="phase-deadline-select double">
						<DatePicker
							buttonCy={'date-button'}
							disabled={disableEditableActions}
							id={task.id}
							task={task}
							isNewDateRange={true}
							startDate={Util.CreateNonUtcMomentDate(task.startYear, task.startMonth, task.startDay)}
							endDate={Util.CreateNonUtcMomentDate(task.deadlineYear, task.deadlineMonth, task.deadlineDay)}
							handleDateRangeChange={this.handleDateRangeChange.bind(this)}
							clearable={true}
							clearBothDates={this.clearDates.bind(this)}
							startFrom={Util.getStartFrom(task, this.props.intl)}
							endFrom={Util.getEndFrom(task, this.props.intl)}
							datePickerStyle={DATE_PICKER_STYLE.SCOPING_PHASE_DATE}
							colorInherited={true}
							fontWeight={'normal'}
							collapseOnWheel={true}
							compactShowYear={this.props.compactShowYear}
							disabledDates={isDateDisabled}
						/>
					</div>
				);
			case 'sprint':
				if (!this.props.lazyDataFetched) {
					return (
						<TextTile
							textAlign={col.align}
							task={task}
							id={task.id}
							key={'sprint'}
							value={task.sprint ? task.sprint.name : formatMessage({id: 'project_sprints.backlog'})}
						/>
					);
				}
				const sprintOptions = [];
				sprintOptions.push({label: formatMessage({id: 'project_sprints.backlog'}), value: null});
				if (this.props.sortedSprints) {
					this.props.sortedSprints.forEach(sprintOption => {
						sprintOptions.push({
							label: sprintOption.label,
							value: sprintOption.value,
						});
					});
				}
				return (
					<Dropdown
						focusOnClick={true}
						optionClickEvent={true}
						onChange={this.onSprintChange.bind(this)}
						options={sprintOptions}
						value={
							task.sprint ? (this.props.viewer.project ? task.sprint.id : task.sprint.projectGroupSprintId) : null
						}
						hideLabel={true}
						disabled={disableEditableActions}
						listDataCy={'sprint-list'}
						buttonCy={'sprint-arrow'}
						inputCy={'sprint-input'}
						customHeight={30}
					/>
				);
			case 'phase':
				if (!this.props.lazyDataFetched) {
					return (
						<TextTile
							textAlign={col.align}
							task={task}
							id={task.id}
							key={'sprint'}
							value={task.phase ? task.phase.name : formatMessage({id: 'project_scopes.no-scope'})}
						/>
					);
				}
				const phaseOptions = [];
				phaseOptions.push({label: formatMessage({id: 'project_scopes.no-scope'}), value: null});
				taskProject.phases?.edges.forEach(phase => {
					phaseOptions.push({
						label: phase.node.name,
						value: phase.node.id,
					});
				});
				return (
					<Dropdown
						focusOnClick={true}
						optionClickEvent={true}
						onChange={this.onPhaseChange.bind(this)}
						options={phaseOptions}
						value={task.phase ? task.phase.id : null}
						hideLabel={true}
						disabled={disableEditableActions || this.props.isClientUser}
						listDataCy={'sprint-list'}
						buttonCy={'sprint-arrow'}
						inputCy={'sprint-input'}
						customHeight={30}
					/>
				);
			case 'labels':
				return <LabelGroup labels={task.taskLabels} noWrap={true} />;
			case 'approved':
				if (col.hide) {
					return null;
				} else {
					return (
						<Dropdown
							customClasses={'approved-dropdown'}
							focusOnClick={true}
							optionClickEvent={true}
							onChange={option => this.onTaskApprovalChange(task, option)}
							options={[
								{
									label: formatMessage({id: 'common.yes'}),
									value: true,
								},
								{
									label: formatMessage({id: 'common.no'}),
									value: false,
								},
							]}
							value={task.approved}
							hideLabel={true}
							disabled={disableEditableActions || !hasPermission(PERMISSION_TYPE.PHASE_UPDATE)}
							listDataCy={'approved-list'}
							buttonCy={'approved-arrow'}
							inputCy={'approved-input'}
							restrictWidth={true}
							customHeight={30}
						/>
					);
				}
			case 'chip-right':
				return (
					<div className={'last-cell' + (groupByPerson ? ' grouped' : '')} style={{height: this.props.rowHeight}}>
						<div className="chip-container ">
							<div
								className={
									'chip-div' +
									(!this.props.selected && !this.props.isHighlighted && this.props.noBorderRight
										? ' no-border-right'
										: '')
								}
							/>
							<div className={'chip ' + (!this.props.emptyPhase ? 'has-phase' : '')} />
						</div>
					</div>
				);
			default:
				return null;
		}
	}

	render() {
		let rowElements = [];
		const disabled =
			this.props.projectLocked || this.props.disabled || isProjectLocked(this.props.task && this.props.task.project);
		if (this.props.task) {
			const task = this.props.task;
			const {formatMessage} = this.props.intl;
			this.estimationUnit =
				task && task.project
					? task.project.estimationUnit === 'HOURS'
						? formatMessage({id: 'common.hours.short'})
						: formatMessage({id: 'common.points.short'})
					: this.props.isEstimatedInHours
					? formatMessage({id: 'common.hours.short'})
					: formatMessage({id: 'common.points.short'});

			this.warnings = this.props.task ? Util.GetTaskWarnings(task, this.props.intl) : null;
			this.path =
				(window.location.pathname.endsWith('/') ? window.location.pathname : window.location.pathname + '/') +
				'T' +
				task.companyTaskId;
			let taskId;
			let selector;
			let novaControl;
			rowElements = [];
			this.props.availableColumns.forEach(col => {
				if (col.checked || col.maintainSpace) {
					if (col.name === 'task-id') {
						taskId = (
							<div
								className={col.name}
								data-cy={col.name}
								key={col.name}
								style={{
									maxWidth: col.maxWidth ? col.maxWidth : '',
									minWidth: col.minWidth,
								}}
								ref={node => (this.taskIdNode = node)}
								tabIndex={'0'}
								onKeyDown={
									task ? e => (e.key === ' ' || e.key === 'Enter' ? this.openCardModal(e, task) : null) : null
								}
								onClick={task ? e => this.onTaskClick(e, task, 'id') : null}
							>
								{'T' + task.companyTaskId}
							</div>
						);
						return;
					}
					// Extracted rather than pushed as a cell of its own, the same as the selector and the
					// task id below: all three live inside the task-name cell's own leading cluster, and
					// a generic cell here would put the control in a column the header does not have.
					if (col.name === 'nova') {
						novaControl = this.getNovaControlForColumn(col, task);
						return;
					}
					if (col.name === 'selector') {
						selector = (
							<CheckBox
								id={task.id}
								key={'selector'}
								isDisabled={isProjectDone(task.project)}
								isChecked={this.props.selected}
								isFocusable={!isProjectDone(task.project)}
								onClick={this.handleSelected.bind(this)}
								cy={'selector-checkbox'}
								userpilot={'bulk-update-checkbox'}
							/>
						);
						return;
					}
					rowElements.push({
						name: col.name,
						element: this.getElemForCol(col, taskId, selector, novaControl),
						minWidth: col.minWidth,
						maxWidth: col.maxWidth ? col.maxWidth : '',
						justifyContent: col.align,
					});
				}
			});
			const taskCellMaxWidth = this.props.taskCellMaxWidth;
			const leadingWidth = leadingCellWidth(taskCellMaxWidth, !!novaControl);
			const content = (
				<div
					onBlur={this.onRowBlur.bind(this)}
					className={
						'table-row-v3' +
						(this.props.isHighlighted ? ' highlight' : '') +
						(this.props.isNewTask ? ' animated pulse 0.2s' : '') +
						(this.props.selected ? ' selected ' : '') +
						(disabled ? ' locked' : '') +
						(this.props.parentType ? ' ' + this.props.parentType : '') +
						(this.props.task && this.props.task.favoured && this.props.task.approved ? ' favoured' : '') +
						(this.props.dragActive ? ' no-hover' : '')
					}
					data-cy={'task-scoping-table-row-v3'}
					ref={this.props.innerRef}
					style={{
						height: this.props.rowHeight,
					}}
					onContextMenu={e => this.props.onContextMenu(this.props.task, e)}
				>
					<div className={'drag-handle' + (disabled || this.props.disableDragHandle ? ' disabled' : '')} />
					{rowElements.map((obj, index) => (
						<div
							key={index}
							className={
								'elem-wrapper' +
								(disabled ? ' locked' : '') +
								(!obj.maxWidth ? ' growable' : '') +
								(index === rowElements.length - 1 ? ' last' : '')
							}
							style={{
								height: this.props.rowHeight,
								minWidth: obj.minWidth,
								maxWidth: obj.maxWidth
									? obj.maxWidth
									: obj.name === 'task-name'
									? leadingWidth
									: taskCellMaxWidth,
								justifyContent: obj.justifyContent,
							}}
						>
							{obj.element}
						</div>
					))}
					{!task.approved ? null : (
						<div className={'star-option'}>
							<StarTask
								disabled={this.props.selectionMode}
								task={task}
								parentId={this.props.viewer.id}
								favoriteTaskList={this.props.favoriteTaskList}
							/>
						</div>
					)}
					{disabled || !this.props.lazyDataFetched || this.props.selectionMode ? null : (
						<div
							data-cy="dot-options"
							className={'dot-options' + (this.props.contextMenuTaksId === task.id ? ' clicked' : '')}
							onClick={this.toggleContextMenu.bind(this)}
						/>
					)}
				</div>
			);

			return this.props.withMargin ? <div className={'table-row-v3-margin-wrapper'}>{content}</div> : content;
		}
	}
}

TableRow.propTypes = {
	id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	task: PropTypes.object,
	index: PropTypes.number,
	tableRowElems: PropTypes.array,
	rowHeight: PropTypes.number.isRequired,
	disabled: PropTypes.bool,
	shouldHaveBorderLeft: PropTypes.bool,
	onRowSelected: PropTypes.func,
	noBorderRight: PropTypes.bool,
	isHighlighted: PropTypes.bool,
	selected: PropTypes.bool,
};
TableRow.defaultProps = {
	lazyDataFetched: true,
};
export default injectIntl(TableRow);

// Raw export for unit tests: the default export is wrapped in injectIntl and the component is far
// too heavy to render, but getElemForCol is a plain method whose column elements can be built from
// an instance. Mirrors the pattern top_section.js uses for the same reason.
export {TableRow};
